import config from "@/config/config";
import { openApi } from "@/config/httpClient";
import store from "@/config/store";
import { BookableTimeSlot, BookingResult, CreateTicketDTO, Ticket } from "@/generated/openapi";
import { asTicketOwner } from "@/models/PersonalData";
import { asDateTime } from "@/utils/dateTimeUtils";
import { AxiosResponse } from "axios";
import { captchaService } from "./captchaService";


class UserService {


  private transactionIdOrReject() {
    const transactionId = store.state.transactionId
    return transactionId ? Promise.resolve(transactionId) : Promise.reject()
  }

  public getAllRooms() {
    return openApi.findRooms(undefined, undefined, config.maxRooms, 0)
      .then(resp => resp.data.content || [])
  }

  public getRoom(id: string) {
    return openApi.getRoom(id)
      .then(resp => resp.data)
  }
  public findTimeSlots(roomId: string, fromDate: string|undefined, toDate: string|undefined): Promise<BookableTimeSlot[]> {
    const transactionId = store.state.transactionId
    if (transactionId) {
      return openApi.getAvailableTimeSlotsByTransaction(transactionId, roomId, fromDate, toDate).then(resp => resp.data)
    } else {
      return openApi.getAvailableTimeSlotsByRoom(roomId, fromDate, toDate).then(resp => resp.data)
    }
  }

  addToShoppingCart(timeSlotId: string) {
    const transactionId = store.state.transactionId
    let promise;
    if (transactionId) {
      promise = openApi.addTicket(transactionId, {
        timeSlotId
      })
    } else {
      promise = captchaService.getToken().then(token => {
        return openApi.createTransaction({
          timeSlotId,
        } as CreateTicketDTO, token)
      })
    }
    return promise.then(resp => resp.data)
      .then(data => {
        if (!data || !data.transactionId || !data.expiredAt || !data.ticket) {
          throw new Error()
        }
        store.commit('setTransactionId', data.transactionId)
        store.commit('addTransactionTicket', data.ticket)
        store.commit('setTransactionExpiration', asDateTime(data.expiredAt))
        return data
      }).catch((err) => new Promise((_resolve, reject) => {
        this.refreshShoppingCart().then(() => reject(err))
      }))
  }

  removeFromShoppingCart(ticketId: string) {
    return this.transactionIdOrReject()
      .then(transactionId => {
        return openApi.removeTicketByTransaction(transactionId, ticketId)
      })
  }

  shoppingCartRefreshTimeout() {
    const transactionId = store.state.transactionId
    if (transactionId) {
      return openApi.touchTransaction(transactionId)
    } else {
      return Promise.reject()
    }
  }

  resetShoppingCart(): Promise<void> {
    let promise: Promise<any> = Promise.resolve()
    const transactionId = store.state.transactionId
    if (transactionId) {
      promise = openApi.removeTransaction(transactionId)
    }
    return promise.then(() => {
      store.commit('clearTransaction')
      store.commit('clearPersonalData')
      return
    })
  }

  refreshShoppingCart(): Promise<void> {
    const transactionId = store.state.transactionId
    return new Promise(resolve => {
      if (!transactionId) {
        store.commit('clearTransaction')
        resolve()
      } else {
        openApi.getTransactionSummary(transactionId)
          .then(resp => resp.data)
          .then(data => {
            if (!data || !data.transactionId || !data.expiredAt || !data.tickets || data.tickets.length < 1) {
              throw new Error()
            }
            store.commit('setTransactionId', data.transactionId)
            store.commit('setTransactionTickets', data.tickets)
            store.commit('setTransactionExpiration', asDateTime(data.expiredAt))
          }).catch(() => {
            store.commit('clearTransaction')
          }).finally(() => {
            resolve()
          })
      }
    })
  }
  bookShoppingCart(): Promise<BookingResult> {
    return new Promise((resolve, reject) => {
      const transactionId = store.state.transactionId
      const personalData = store.state.personalData
      if (!transactionId || !personalData) {
        reject()
        return
      }
      const ticketOwner = asTicketOwner(personalData)
      ticketOwner.language = config.language
      openApi.bookTransaction(transactionId, ticketOwner)
        .then(resp => resp.data)
        .then(data => {
          store.commit('clearPersonalData')
          resolve(data)
        }).catch((err) => {
          reject(err)
        })
    })
  }

  getTicketsForReservationId(ownerId: string): Promise<AxiosResponse<Ticket[]>> {
    return openApi.getTicketsByOwner(ownerId)
  }

  removeTicketsFromReservation(ownerId: string, ticketIds: string[]): Promise<void> {
    return openApi.deleteTicketsByOwner(ownerId, ticketIds.join(',')).then(resp => resp.data)
  }
}

const userService = new UserService();

export {
  userService
};
